import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import ArrowDownward from '@material-ui/icons/ArrowDownward';
import HighlightOff from '@material-ui/icons/HighlightOff';
import CancelPresentation from '@material-ui/icons/CancelPresentation';
import { Content, Header, Page } from '@backstage/core-components';
import { useApi, fetchApiRef, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef, stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { formatInstantInUserTimezone } from '../../utils/formatInstantInUserTimezone';
import {
  effectiveRequestStatus,
  requestDocIndicatesRejected,
  requestDocIndicatesUserCancelled,
  requestDocIndicatesWorkflowComplete,
} from '../../utils/requestRejection';
import { userCancellationAllowedByTimeline } from '../../utils/requestCancellationEligibility';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';
import { useCatalogEditAllowed } from '../../hooks/useCatalogEditAllowed';

export interface RequestDoc {
  request_id: number;
  request_name: string;
  requested_by: string;
  status: 'open' | 'in-progress' | 'closed';
  /** Derived by API from audit (`request_rejected`); not a Firestore field. */
  rejected?: boolean;
  /** Derived by API from audit (`request_cancelled_by_user`); not a Firestore field. */
  cancelled_by_user?: boolean;
  scaffolder_task_id?: string;
  template_entity_ref?: string;
  payload: Record<string, unknown>;
  service_now: Record<string, unknown>;
  audit: {
    created_at: string;
    last_updated_at: string;
    last_updated_status: Array<{ event: string; timestamp?: string }>;
  };
}

const BACKSTAGE_USER_EMAIL_HEADER = 'X-Backstage-User-Email';

function emailFromUserEntity(entity: Entity): string | undefined {
  const spec = entity.spec as { profile?: { email?: string }; email?: string } | undefined;
  const a = entity.metadata?.annotations;
  return (
    spec?.profile?.email ||
    spec?.email ||
    a?.['backstage.io/email'] ||
    a?.email ||
    a?.['gitlab.com/user-email'] ||
    ''
  ).trim() || undefined;
}

/** If `raw` is a User entity ref (or a bare catalog name), return a string suitable for getEntityByRef. */
function userEntityRefForLookup(raw: string): string | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  try {
    const ref = parseEntityRef(t);
    if (ref.kind.toLowerCase() === 'user') {
      return stringifyEntityRef(ref);
    }
  } catch {
    // not a full ref
  }
  if (/^[\w.-]+$/i.test(t) && !t.includes(':')) {
    return stringifyEntityRef({ kind: 'User', namespace: 'default', name: t });
  }
  return undefined;
}

function RequestedByLine({ raw }: { raw: string }) {
  const catalogApi = useApi(catalogApiRef);
  const [display, setDisplay] = useState(raw);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const trimmed = raw.trim();
      if (!trimmed) {
        setDisplay('');
        return;
      }
      if (trimmed.includes('@')) {
        setDisplay(trimmed);
        return;
      }
      const refStr = userEntityRefForLookup(trimmed);
      if (!refStr) {
        setDisplay(trimmed);
        return;
      }
      try {
        const entity = await catalogApi.getEntityByRef(refStr);
        if (cancelled) return;
        if (!entity) {
          setDisplay(trimmed);
          return;
        }
        const em = emailFromUserEntity(entity);
        setDisplay(em ?? trimmed);
      } catch {
        if (!cancelled) setDisplay(trimmed);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [raw, catalogApi]);

  return <>{display}</>;
}

/** Wait for auth/session to be ready (e.g. after K8s load). */
async function waitForIdentity(
  identityApi: { getProfileInfo: () => Promise<{ email?: string }> },
  maxAttempts = 4,
  delayMs = 400,
): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      await identityApi.getProfileInfo();
      return;
    } catch {
      if (i < maxAttempts - 1) await new Promise(r => setTimeout(r, delayMs));
    }
  }
}

/** Fetch with one retry on 401 to handle session/cookie timing in K8s. */
async function fetchWithRetryOn401(
  fetchApi: { fetch: (url: string, init?: { headers?: Record<string, string> }) => Promise<Response> },
  url: string,
  headers: Record<string, string> | undefined,
  retryDelayMs = 600,
): Promise<Response> {
  let res = await fetchApi.fetch(url, headers ? { headers } : undefined);
  if (res.status === 401 && retryDelayMs > 0) {
    await new Promise(r => setTimeout(r, retryDelayMs));
    res = await fetchApi.fetch(url, headers ? { headers } : undefined);
  }
  return res;
}

/** All possible status steps in display order. Show every step; mark done (blue) or inactive (grey). */
const ALL_STATUS_STEPS = [
  'backstage_ticket_created',
  'service_now_ticket_created',
  'automation_started',
  'automation_completed',
  'service_now_ticket_closed',
  'access_provisioning_completed',
] as const;

/** Alternative event names that may be written to Firestore; map to canonical step for timeline. */
const EVENT_ALIASES: Record<string, (typeof ALL_STATUS_STEPS)[number]> = {
  service_now_ticket_updated: 'service_now_ticket_created',
  servicenow_ticket_created: 'service_now_ticket_created',
  servicenow_ticket_updated: 'service_now_ticket_created',
};

/** Steps shown in the vertical timeline (hide ServiceNow created/closed only). */
const STATUS_TIMELINE_UI_STEPS = ALL_STATUS_STEPS.filter(
  step => step !== 'service_now_ticket_created' && step !== 'service_now_ticket_closed',
);

/** Normalize event name for lookup: lowercase, trim, spaces -> underscores (Firestore may store "service now ticket created"). */
function normalizeEventKey(name: string): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, '_');
}

/** Sort by timestamp chronologically; events without timestamp go at the end. */
export function sortTimelineEvents(
  events: Array<{ event: string; timestamp?: string }>,
): Array<{ event: string; timestamp?: string }> {
  if (!events?.length) return [];
  return [...events].sort((a, b) => {
    const ta = a.timestamp?.trim() ? new Date(a.timestamp).getTime() : Infinity;
    const tb = b.timestamp?.trim() ? new Date(b.timestamp).getTime() : Infinity;
    if (ta !== tb) return ta - tb;
    return 0;
  });
}

function StatusTimeline({ events }: { events: Array<{ event: string; timestamp?: string }> }) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const inactiveColor =
    theme.palette.type === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400];
  const inactiveBg =
    theme.palette.type === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200];

  const byEvent = new Map<string, { event: string; timestamp?: string }>();
  (events ?? []).forEach(e => {
    const key = normalizeEventKey(e.event);
    if (key) byEvent.set(key, e);
  });

  function getEventForStep(step: (typeof ALL_STATUS_STEPS)[number]): { event: string; timestamp?: string } | undefined {
    const stepLower = step.toLowerCase();
    if (byEvent.has(stepLower)) return byEvent.get(stepLower);
    for (const [aliasKey, canonicalStep] of Object.entries(EVENT_ALIASES)) {
      if (canonicalStep === step && byEvent.has(aliasKey)) return byEvent.get(aliasKey);
    }
    return undefined;
  }

  return (
    <Box display="flex" flexDirection="column">
      {STATUS_TIMELINE_UI_STEPS.map((step, index) => {
        const found = getEventForStep(step);
        const done = Boolean(found);
        const timestamp = found?.timestamp?.trim();
        const isLast = index === STATUS_TIMELINE_UI_STEPS.length - 1;
        let stepBackground: string;
        if (done) {
          stepBackground =
            theme.palette.type === 'dark' ? `${primaryColor}22` : `${primaryColor}18`;
        } else {
          stepBackground = inactiveBg;
        }
        let stepCaption: ReactNode = null;
        if (done && timestamp) {
          stepCaption = (
            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
              {formatInstantInUserTimezone(timestamp)}
            </Typography>
          );
        } else if (!done) {
          stepCaption = (
            <Typography variant="caption" style={{ color: inactiveColor, marginTop: 4 }} display="block">
              Inactive
            </Typography>
          );
        }
        return (
          <Box key={step} display="flex" flexDirection="column" alignItems="stretch">
            <Box
              padding={1.5}
              style={{
                borderLeft: `4px solid ${done ? primaryColor : inactiveColor}`,
                backgroundColor: stepBackground,
                borderRadius: 4,
              }}
            >
              <Typography
                variant="body2"
                style={{
                  fontWeight: done ? 600 : 400,
                  color: done ? theme.palette.text.primary : inactiveColor,
                }}
              >
                {step.replace(/_/g, ' ').replace(/\bbackstage\b/gi, 'Iron Phoenix')}
              </Typography>
              {stepCaption}
            </Box>
            {!isLast && (
              <Box display="flex" justifyContent="center" paddingY={0.25} style={{ color: inactiveColor }}>
                <ArrowDownward style={{ fontSize: 20 }} />
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export const RequestDetailPage = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [request, setRequest] = useState<RequestDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const adminMember = useAdminGroupMember();
  const templateRefForEdit =
    typeof request?.template_entity_ref === 'string' ? request.template_entity_ref : '';
  const editAllowed = useCatalogEditAllowed(templateRefForEdit);
  const theme = useTheme();
  const primary = theme.palette.primary;

  useEffect(() => {
    let cancelled = false;
    waitForIdentity(identityApi).then(() => {
      if (!cancelled) setAuthReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const load = useCallback(async () => {
    if (!requestId || !/^\d+$/.test(requestId)) {
      setError('Invalid request ID');
      setLoading(false);
      return;
    }
    if (!authReady) return;
    setError(null);
    setLoading(true);
    try {
      const baseUrl = config.getString('backend.baseUrl');
      const url = `${baseUrl}/api/requests/requests/${requestId}`;
      const headers: Record<string, string> = {};
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email?.trim();
        if (email && email.includes('@')) headers[BACKSTAGE_USER_EMAIL_HEADER] = email;
      } catch {
        /* ignore */
      }
      const res = await fetchWithRetryOn401(fetchApi, url, Object.keys(headers).length > 0 ? headers : undefined);
      if (!res.ok) {
        if (res.status === 404) {
          setError('Request not found');
          setRequest(null);
          return;
        }
        if (res.status === 401) {
          setError('Sign in required. Please refresh the page or sign in again to view this request.');
          setRequest(null);
          return;
        }
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as RequestDoc;
      setRequest(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load request');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, config, fetchApi, identityApi, authReady]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancel = useCallback(async () => {
    if (!requestId || !/^\d+$/.test(requestId)) return;
    setCancelError(null);
    setCancelSubmitting(true);
    try {
      const baseUrl = config.getString('backend.baseUrl');
      const url = `${baseUrl}/api/requests/requests/${requestId}/cancel`;
      const headers: Record<string, string> = {};
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email?.trim();
        if (email && email.includes('@')) headers[BACKSTAGE_USER_EMAIL_HEADER] = email;
      } catch {
        /* ignore */
      }
      const res = await fetchApi.fetch(url, {
        method: 'POST',
        headers: Object.keys(headers).length > 0 ? headers : undefined,
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = (await res.json()) as { error?: string };
          if (j?.error) msg = j.error;
        } catch {
          const body = await res.text();
          if (body) msg = body;
        }
        throw new Error(msg);
      }
      const data = (await res.json()) as RequestDoc;
      setRequest(data);
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setCancelSubmitting(false);
    }
  }, [requestId, config, fetchApi, identityApi]);

  if (loading) {
    return (
      <Page themeId="home">
        <Header title="Request" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (error || !request) {
    return (
      <Page themeId="home">
        <Header title="Request" />
        <Content>
          <Box p={3}>
            <Typography color="error">{error || 'Request not found'}</Typography>
            <Box mt={2}>
              <Link to="/requests">Back to Requests</Link>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  const scaffolderTaskId = request.scaffolder_task_id?.trim();
  const showScaffolderTaskLink =
    Boolean(scaffolderTaskId) && (adminMember === true || editAllowed === true);
  const isRejected = requestDocIndicatesRejected(request);
  const userCancelled = requestDocIndicatesUserCancelled(request);
  const displayStatus = effectiveRequestStatus(request);
  const canCancel =
    (request.status === 'open' || request.status === 'in-progress') &&
    !isRejected &&
    !userCancelled &&
    !requestDocIndicatesWorkflowComplete(request) &&
    userCancellationAllowedByTimeline(request.audit?.last_updated_status);

  return (
    <Page themeId="home">
      <Header
        title={`Request ${request.request_id}`}
        subtitle={request.request_name}
      />
      <Content>
        <Box
          pt={2}
          maxWidth={1120}
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          alignItems="flex-start"
          style={{ gap: 24 }}
        >
          <Paper style={{ padding: 24, flex: '1 1 480px', minWidth: 0 }}>
            {cancelError ? (
              <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>
                {cancelError}
              </Typography>
            ) : null}
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="column" style={{ marginBottom: 20 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Requested by:
                </Typography>
                <Typography variant="body1" style={{ paddingLeft: 24 }}>
                  <RequestedByLine raw={request.requested_by} />
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" style={{ marginBottom: 20 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status:
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8, paddingLeft: 24 }}>
                  <Chip
                    label={displayStatus}
                    size="small"
                    color={displayStatus === 'closed' ? 'default' : 'primary'}
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" style={{ marginBottom: 20 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created:
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: 24 }}>
                  {formatInstantInUserTimezone(request.audit?.created_at)}
                </Typography>
              </Box>
              <Box display="flex" flexDirection="column" style={{ marginBottom: showScaffolderTaskLink ? 20 : 0 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last updated:
                </Typography>
                <Typography variant="body2" style={{ paddingLeft: 24 }}>
                  {formatInstantInUserTimezone(request.audit?.last_updated_at)}
                </Typography>
              </Box>
              {showScaffolderTaskLink && scaffolderTaskId ? (
                <Box display="flex" flexDirection="column">
                  <Typography variant="subtitle2" color="textSecondary">
                    Template run
                  </Typography>
                  <Box style={{ paddingLeft: 24 }}>
                    <Typography variant="body2">
                      <Link
                        to={`/create/tasks/${scaffolderTaskId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View scaffolder execution log
                      </Link>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 8, marginBottom: 0 }}>
                      Task ID: <code>{scaffolderTaskId}</code>
                    </Typography>
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Paper>
          <Box
            flex="0 0 auto"
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            style={{ gap: 16, minWidth: 260, maxWidth: 320 }}
          >
            {isRejected ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{
                  gap: 10,
                  padding: '22px 28px',
                  borderRadius: 18,
                  background: `linear-gradient(155deg, ${primary.dark} 0%, ${primary.main} 40%, ${primary.light} 100%)`,
                  color: primary.contrastText,
                  boxShadow: `0 12px 32px ${alpha(primary.main, 0.38)}, 0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.22)`,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <HighlightOff style={{ fontSize: 40, opacity: 0.95, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                <Typography
                  component="div"
                  style={{
                    fontWeight: 800,
                    fontSize: '1.35rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    textShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    lineHeight: 1.2,
                    textAlign: 'center',
                  }}
                >
                  Rejected
                </Typography>
              </Box>
            ) : null}
            {userCancelled ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{
                  gap: 10,
                  padding: '22px 28px',
                  borderRadius: 18,
                  background: `linear-gradient(155deg, ${primary.dark} 0%, ${primary.main} 40%, ${primary.light} 100%)`,
                  color: primary.contrastText,
                  boxShadow: `0 12px 32px ${alpha(primary.main, 0.38)}, 0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.22)`,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <CancelPresentation
                  style={{ fontSize: 40, opacity: 0.95, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
                />
                <Typography
                  component="div"
                  style={{
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    textShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    lineHeight: 1.25,
                    textAlign: 'center',
                  }}
                >
                  cancelled
                  <br />
                  by user
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  style={{
                    opacity: 0.92,
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    letterSpacing: '0.02em',
                    textShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  You withdrew this request
                </Typography>
              </Box>
            ) : null}
            {canCancel ? (
              <Button
                variant="contained"
                color="primary"
                disabled={cancelSubmitting}
                onClick={() => void handleCancel()}
                style={{
                  borderRadius: 14,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  padding: '14px 28px',
                  minHeight: 52,
                  boxShadow: `0 8px 22px ${alpha(primary.main, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.2)`,
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                {cancelSubmitting ? 'Cancelling…' : 'Cancel'}
              </Button>
            ) : null}
          </Box>
          <Box width="100%" mt={2}>
            <Typography variant="h6" gutterBottom>
              Status timeline
            </Typography>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <StatusTimeline events={request.audit?.last_updated_status ?? []} />
            </Paper>
            <Box mt={2}>
              <Link to="/requests">Back to Requests</Link>
            </Box>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
